import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { 
    createSFoldGeometry, 
    createPinchPleatGeometry, 
    createEyeletGeometry, 
    createPencilPleatGeometry,
    createTrackOrRodMesh
} from './proceduralCurtainMesh';
import { createLinenTexture, createVelvetTexture, createSheerPerforationTexture } from './fabricTextures';

export interface VisualizerConfig {
    style: 's-fold' | 'pinch-pleat' | 'eyelet' | 'pencil-pleat';
    width: number;
    height: number;
    openPercent: number;
    fabricType: 'linen' | 'velvet' | 'sheer';
    hardwareStyle: 'architectural' | 'decorative';
    mountType: 'wall' | 'ceiling';
    mode: 'studio' | 'ar';
}

export class CurtainSceneController {
    private renderer: THREE.WebGLRenderer;
    private scene: THREE.Scene;
    private camera: THREE.PerspectiveCamera;
    private controls: OrbitControls;
    
    private curtainMesh: THREE.Mesh | null = null;
    private hardwareMesh: THREE.Group | null = null;
    private studioGroup: THREE.Group;
    
    private animationFrameId: number = 0;

    constructor(private container: HTMLElement) {
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(container.clientWidth, container.clientHeight);
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.0;
        this.renderer.shadowMap.enabled = true;
        
        container.appendChild(this.renderer.domElement);
        
        this.scene = new THREE.Scene();
        
        this.camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 100);
        this.camera.position.set(0, 1.5, 4);
        
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.target.set(0, 1, 0);
        this.controls.maxPolarAngle = Math.PI / 2 + 0.1;
        
        this.studioGroup = new THREE.Group();
        this.scene.add(this.studioGroup);
        
        this.setupLighting();
        this.setupStudio();
        
        window.addEventListener('resize', this.onWindowResize);
        
        this.renderLoop();
    }
    
    private setupLighting() {
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
        this.scene.add(ambientLight);
        
        const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
        directionalLight.position.set(2, 3, 4);
        directionalLight.castShadow = true;
        directionalLight.shadow.mapSize.width = 2048;
        directionalLight.shadow.mapSize.height = 2048;
        this.scene.add(directionalLight);
        
        // Window back light
        const windowLight = new THREE.DirectionalLight(0xccddff, 2.0);
        windowLight.position.set(0, 2, -5);
        this.scene.add(windowLight);
    }
    
    private setupStudio() {
        // Floor
        const floorGeo = new THREE.PlaneGeometry(10, 10);
        const floorMat = new THREE.MeshStandardMaterial({ color: 0x886644, roughness: 0.8 });
        const floor = new THREE.Mesh(floorGeo, floorMat);
        floor.rotation.x = -Math.PI / 2;
        floor.receiveShadow = true;
        this.studioGroup.add(floor);
        
        // Wall
        const wallGeo = new THREE.PlaneGeometry(10, 5);
        const wallMat = new THREE.MeshStandardMaterial({ color: 0xf5f5f5 });
        const wall = new THREE.Mesh(wallGeo, wallMat);
        wall.position.set(0, 2.5, -0.5);
        wall.receiveShadow = true;
        this.studioGroup.add(wall);
    }
    
    public updateCurtain(config: VisualizerConfig) {
        if (this.curtainMesh) {
            this.scene.remove(this.curtainMesh);
            if (this.curtainMesh.geometry) this.curtainMesh.geometry.dispose();
            if (this.curtainMesh.material) {
                const material = this.curtainMesh.material as THREE.MeshPhysicalMaterial;
                if (material.map) material.map.dispose();
                if (material.bumpMap) material.bumpMap.dispose();
                if (material.alphaMap) material.alphaMap.dispose();
                if (material.roughnessMap) material.roughnessMap.dispose();
                material.dispose();
            }
        }
        
        if (this.hardwareMesh) {
            this.scene.remove(this.hardwareMesh);
        }
        
        // Mode switch
        this.studioGroup.visible = config.mode === 'studio';
        this.renderer.setClearColor(config.mode === 'ar' ? 0x000000 : 0x222222, config.mode === 'ar' ? 0 : 1);
        
        // Create Geometry
        let geometry: THREE.BufferGeometry;
        switch (config.style) {
            case 's-fold':
                geometry = createSFoldGeometry(config.width, config.height, 2.0, 15, config.openPercent);
                break;
            case 'pinch-pleat':
                geometry = createPinchPleatGeometry(config.width, config.height, 10, config.openPercent);
                break;
            case 'eyelet':
                geometry = createEyeletGeometry(config.width, config.height, 12, config.openPercent);
                break;
            case 'pencil-pleat':
                geometry = createPencilPleatGeometry(config.width, config.height, 40, config.openPercent);
                break;
            default:
                geometry = createSFoldGeometry(config.width, config.height, 2.0, 15, config.openPercent);
        }
        
        // Create Material
        let material: THREE.MeshPhysicalMaterial;
        if (config.fabricType === 'linen') {
            const linenTex = createLinenTexture();
            material = new THREE.MeshPhysicalMaterial({
                map: linenTex.diffuse,
                bumpMap: linenTex.bump,
                bumpScale: 0.002,
                roughness: 0.9,
                side: THREE.DoubleSide
            });
        } else if (config.fabricType === 'velvet') {
            const velvetTex = createVelvetTexture();
            material = new THREE.MeshPhysicalMaterial({
                color: 0x8a101b,
                roughnessMap: velvetTex,
                roughness: 0.4,
                clearcoat: 0.3,
                clearcoatRoughness: 0.8,
                side: THREE.DoubleSide
            });
        } else {
            // sheer
            const sheerTex = createSheerPerforationTexture();
            material = new THREE.MeshPhysicalMaterial({
                color: 0xffffff,
                alphaMap: sheerTex,
                transparent: true,
                transmission: 0.8,
                opacity: 0.9,
                roughness: 0.5,
                side: THREE.DoubleSide
            });
        }
        
        this.curtainMesh = new THREE.Mesh(geometry, material);
        // center the curtain
        this.curtainMesh.position.set(0, config.height / 2, 0);
        this.curtainMesh.castShadow = true;
        this.scene.add(this.curtainMesh);
        
        // Hardware
        this.hardwareMesh = createTrackOrRodMesh(config.width, config.hardwareStyle, config.mountType);
        this.hardwareMesh.position.set(0, config.height + 0.05, 0);
        this.scene.add(this.hardwareMesh);
    }
    
    public takeScreenshot(): string {
        this.renderer.render(this.scene, this.camera);
        return this.renderer.domElement.toDataURL('image/png');
    }
    
    private onWindowResize = () => {
        this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    }
    
    private renderLoop = () => {
        this.animationFrameId = requestAnimationFrame(this.renderLoop);
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }
    
    public dispose() {
        cancelAnimationFrame(this.animationFrameId);
        window.removeEventListener('resize', this.onWindowResize);
        
        this.renderer.dispose();
        if (this.container.contains(this.renderer.domElement)) {
            this.container.removeChild(this.renderer.domElement);
        }
    }
}
